import type { RootState } from '@/app/store';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DrawerConfig =
  | {
      type: 'affectation-form';
      title: string;
      payload?: {
        affectationId: number;
      };
    }
  | {
      type: 'sus-form';
      title: string;
      payload?: {
        susId: number;
      };
    }
  | {
      type: 'sus-detail';
      title: string;
      payload: {
        susId: number;
      };
    };
// Ajouter de nouveaux types de drawer ici, ex :
// | { type: 'edit-user'; title: string; payload: { userId: string } }

type DrawerState = {
  isOpen: boolean;
  config: DrawerConfig | null;
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: DrawerState = {
  isOpen: false,
  config: null,
};

const drawerSlice = createSlice({
  name: 'drawer',
  initialState,
  reducers: {
    openDrawer: (state, action: PayloadAction<DrawerConfig>) => {
      state.isOpen = true;
      state.config = action.payload;
    },
    closeDrawer: state => {
      state.isOpen = false;
      // On conserve config pendant l'animation de fermeture du drawer
    },
    clearDrawer: state => {
      state.isOpen = false;
      state.config = null;
    },
  },
});

export const { openDrawer, closeDrawer, clearDrawer } = drawerSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectDrawerIsOpen = (state: RootState) => state.drawer.isOpen;
export const selectDrawerConfig = (state: RootState) => state.drawer.config;

// ─── Typed action helpers ─────────────────────────────────────────────────────

/** Ouvre le drawer affectation en mode ajout (sans id) ou édition (avec id). */
export const openAffectationFormDrawer = (title: string, affectationId?: number) =>
  openDrawer({
    type: 'affectation-form',
    title,
    ...(affectationId !== undefined ? { payload: { affectationId } } : {}),
  });

/** Ouvre le drawer service d'urgence en mode ajout (sans id) ou édition (avec `susId`). */
export const openSusFormDrawer = (title: string, susId?: number) =>
  openDrawer({
    type: 'sus-form',
    title,
    ...(susId !== undefined ? { payload: { susId } } : {}),
  });

/** Ouvre le drawer de consultation (lecture seule) d'un service d'urgence. */
export const openSusDetailDrawer = (title: string, susId: number) =>
  openDrawer({ type: 'sus-detail', title, payload: { susId } });

export default drawerSlice.reducer;
